import { useCallback, useMemo } from 'react';
import { EngineService } from '@/services/engine';
import { Event } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { EVENTS_PAGE_SIZE, PaginatedEventsResult } from './eventsDataShared';
import { usePaginatedList } from './usePaginatedList';
import type { PersonOccurrence } from '@/types/person';

export interface UsePersonEventsDataOptions {
    personId: string;
}

export interface PersonEventsDataResult extends PaginatedEventsResult<Event> {
    occurrences: PersonOccurrence[];
}

export function usePersonEventsData({
    personId,
}: UsePersonEventsDataOptions): PersonEventsDataResult {
    const cacheKey = `person-events-${personId}`;

    const fetchPage = useCallback(
        (pageIndex: number): Promise<PaginatedResponse<PersonOccurrence>> =>
            EngineService.getPersonOccurrences(personId, {
                page: pageIndex + 1,
                size: EVENTS_PAGE_SIZE,
            }),
        [personId],
    );

    const result = usePaginatedList<PersonOccurrence>({
        cacheKey,
        pageSize: EVENTS_PAGE_SIZE,
        fetchPage,
    });

    const occurrences = result.events;
    const events = useMemo(
        () => occurrences.map((occurrence) => occurrence.event),
        [occurrences],
    );

    return { ...result, events, occurrences };
}
